import {
  FormDialogSubmitHandler,
  Icon,
  OutlinedInput,
  RHFCheckbox,
  TextEllipsis,
} from '@aibees/shared/components';
import { Member, MemberStatus } from '@aibees/shared/definition';
import { notificationIconMap } from '@aibees/shared/features/notifications/config';
import { useNotify } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { useUpdateWorkspace } from '@aibees/shared/workspace';
import { Box, Grid, Stack, Typography } from '@mui/material';
import { capitalize, random } from 'lodash-es';
import { memo } from 'react';
import { useDeleteMember } from '../../api/use-delete-member';
import { config, scheme } from './config';
import * as Styled from './styles';
import * as SX from './sx';
import type { DeleteMemberDialogProps as Props } from './types';

export const DeleteMemberDialog = memo(
  ({ className, item, onClose }: Props) => {
    const notify = useNotify();
    const api = useDeleteMember(item.user?.id);
    const updateWorkspace = useUpdateWorkspace();

    const handleSubmit: FormDialogSubmitHandler<Member> = (data, e) => {
      e.startProgress();
      api
        .mutateAsync({
          member: { ...data, ...{ status: MemberStatus.Deleted } },
        })
        .then((workspace) => {
          updateWorkspace(workspace);
          e.toggle();
          notify.warning(
            'User deleted!',
            `<b>${item.user?.firstName} ${item.user?.lastName}</b> Account has been deleted.`,
            random(1, 100),
            {
              icon: notificationIconMap.UserManagement!,
            }
          );
        })
        .catch(() => {
          e.endProgress();
        });
    };

    return (
      <Styled.Wrapper
        className={classnames('EditRoleDialog', className)}
        {...config}
        submitButtonProps={{
          children: 'Delete',
        }}
        scheme={scheme}
        defaultOpen
        defaultValues={{ type: item.type, confirmed: false }}
        onClose={onClose}
        onSubmit={handleSubmit}
        contentSx={SX.content}
        hideCloseIconInHeader
        maxWidth="sm"
        fullWidth
      >
        <Stack gap={2}>
          <Typography variant="body4" fontWeight={600} color="black">
            Are you sure you want to delete the disabled user?
          </Typography>
          <Typography variant="body4">
            Please note that deleting a user is irreversible.
          </Typography>
          <Stack gap={1}>
            <Grid container justifyContent="space-between">
              <Typography variant="body4" fontWeight={600} color="black">
                Delete Requests
              </Typography>
              <Box display="flex">
                <Icon
                  size={18}
                  name="email-unread"
                  sxx={{ color: (p) => p.palette.grey[500], marginRight: 0.5 }}
                />
                <TextEllipsis variant="body4" w="160px">
                  {item.user?.username}
                </TextEllipsis>
              </Box>
            </Grid>
            <OutlinedInput
              name="type"
              size="small"
              autoComplete="off"
              disabled
              value={`${item.user?.firstName} ${
                item.user?.lastName
              } (${capitalize(item.type)})`}
              label="First Name"
              startIcon="edit-role"
            />
          </Stack>
          <Stack>
            <RHFCheckbox
              name="confirmed"
              label="Confirm Deleting account"
              sx={SX.checkbox}
              required
            />
          </Stack>
        </Stack>
      </Styled.Wrapper>
    );
  }
);

DeleteMemberDialog.displayName = 'DeleteMemberDialog';
